import { noop } from 'lodash';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { reportErrorToBugsnag } from '@air/utils-error';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { WorkspaceAgentShareLinkPillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentShareLinkPillLeadingVisual';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { WorkspaceAgentShareLinkPreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentShareLinkPreviewCard';
import { useOpenWorkspaceAgentShareLinkSettings } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentShareLinkSettings';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentShareLinkMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentShareLinkMoniker';
import { isWorkspaceAgentShareLinkMonikerUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentShareLinkMonikerUnresolvableError';
import { toWorkspaceAgentShareLinkDisplayUrl } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentShareLinkMonikerSubject';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';
import { getFullShareUrl } from '~/utils/shareUrl';

export type PrivateWorkspaceAgentPanelMessageShareLinkMonikerProps = {
  shortId: string;
};

const reportedShareLinkShortIds = new Set<string>();

const reportShareLinkMonikerErrorOnce = (shortId: string, error: unknown) => {
  if (reportedShareLinkShortIds.has(shortId)) {
    return;
  }

  reportedShareLinkShortIds.add(shortId);
  reportErrorToBugsnag({ error, context: 'Workspace agent share link moniker could not resolve its short url' });
};

export const PrivateWorkspaceAgentPanelMessageShareLinkMoniker = memo(
  ({ shortId }: PrivateWorkspaceAgentPanelMessageShareLinkMonikerProps) => {
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const { copyTextToClipboard } = useCopyTextToClipboard();
    const { openShareLinkSettings } = useOpenWorkspaceAgentShareLinkSettings();

    const focusTrigger = useCallback(() => {
      triggerRef.current?.focus();
    }, []);

    const { inView, ref } = useInView({ triggerOnce: true });

    const {
      data: subject,
      error,
      isLoading,
    } = usePrivateWorkspaceAgentShareLinkMoniker({ shortId }, { enabled: inView });

    const shareUrl = getFullShareUrl(shortId);
    const displayUrl = toWorkspaceAgentShareLinkDisplayUrl(shareUrl);

    const { closeCard, handleOpenChange, isCardOpen, isNavigatingAway, navigateFromCard } =
      useWorkspaceAgentPreviewCardDisclosure({
        focusNavigationOrigin: focusTrigger,
        hasSubject: !!subject,
        onCardOpen: noop,
      });

    useEffect(() => {
      if (error && !isWorkspaceAgentShareLinkMonikerUnresolvableError(error)) {
        reportShareLinkMonikerErrorOnce(shortId, error);
      }
    }, [error, shortId]);

    const handleCopyLink = useCallback(() => {
      void copyTextToClipboard({
        successMessage: 'Share link copied to clipboard',
        text: shareUrl,
      });
    }, [copyTextToClipboard, shareUrl]);

    const handleOpenLink = useCallback(() => {
      closeCard();
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }, [closeCard, shareUrl]);

    const settingsTarget = subject?.settingsTarget ?? null;

    const handleOpenSettings = useCallback(() => {
      if (!settingsTarget) {
        return;
      }

      navigateFromCard(() => {
        openShareLinkSettings(settingsTarget);
      });
    }, [navigateFromCard, openShareLinkSettings, settingsTarget]);

    let chip = <WorkspaceAgentPendingPill label="Share link" />;

    if (subject) {
      chip = (
        <WorkspaceAgentReferencePill
          card={
            <WorkspaceAgentShareLinkPreviewCard
              closeButton={<WorkspaceAgentPreviewCardCloseButton onClose={closeCard} />}
              displayUrl={displayUrl}
              onCopyLink={handleCopyLink}
              onOpenLink={handleOpenLink}
              onOpenSettings={settingsTarget ? handleOpenSettings : undefined}
              previewMedia={subject.previewMedia}
              title={subject.title}
            />
          }
          isOpen={isCardOpen}
          leadingVisual={<WorkspaceAgentShareLinkPillLeadingVisual previewMedia={subject.previewMedia} />}
          onOpenChange={handleOpenChange}
          onTriggerClick={handleCopyLink}
          shouldPreventCloseAutoFocus={isNavigatingAway}
          surface="history"
          testId="WORKSPACE_AGENT_MESSAGE_SHARE_LINK_PILL"
          title={subject.title}
          triggerAriaLabel={`Copy share link ${subject.title}`}
          triggerClassName="py-px pr-1.5 pl-px"
          triggerRef={triggerRef}
          triggerTestId="WORKSPACE_AGENT_MESSAGE_SHARE_LINK_PILL_TRIGGER"
        />
      );
    } else if (error && !isLoading) {
      chip = <WorkspaceAgentUnavailablePill kind="shareLink" referenceId={shortId} />;
    }

    return (
      <span className="inline-flex max-w-full items-center align-middle leading-none" ref={ref}>
        {chip}
      </span>
    );
  },
);

PrivateWorkspaceAgentPanelMessageShareLinkMoniker.displayName = 'PrivateWorkspaceAgentPanelMessageShareLinkMoniker';
